import React, { useState, useRef, useEffect } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import * as Sentry from '@sentry/react';
import { useTranslation } from 'react-i18next';

import { createImageUrl } from '../../services/data';
import { formatTime } from '../../services/formatFunctions';
import { safePlay } from '../../utils/errorHandler';
import { trackAudioPlayed, trackAudioFinished } from '../../mixpanel';
import { useParams } from 'react-router-dom';
import { palette } from '../../themes/theme';

interface WalkingAudioPlayerProps {
  audio: string;
  name?: string;
}

const WalkingAudioPlayer: React.FC<WalkingAudioPlayerProps> = ({audio, name}) => {
    const [isPlaying, setIsPlaying] = useState<boolean>(false)
    const [duration, setDuration] = useState<number>(0)
    const [currentTime, setCurrentTime] = useState<number>(0);
    const [hasError, setHasError] = useState<boolean>(false);
    const audioRef = useRef<HTMLAudioElement>(null)
    const hasTrackedPlay = useRef(false)
    const {slug} = useParams()
    const { t } = useTranslation();

    useEffect(() => {
        if (!isPlaying) {
            audioRef.current?.pause();
        } else {
            safePlay(audioRef.current);
            if (!hasTrackedPlay.current) {
                hasTrackedPlay.current = true;
                trackAudioPlayed(name ?? '');
            }
        }

        return () => {
            audioRef.current?.pause();
        };
    }, [isPlaying]) // eslint-disable-line react-hooks/exhaustive-deps

    const handleLoadeMetaData = () => {
        if (!audioRef.current) return
        setDuration(audioRef.current.duration)
    }

    const handleTimeUpdate = () => {
        if (!audioRef.current) return
        setCurrentTime(audioRef.current.currentTime)
    }

    const handleSeek = (time: number) => {
        if (!audioRef.current) return
        audioRef.current.currentTime = time
    }

    const handleAudioError = () => {
        setHasError(true);
        setIsPlaying(false);
        Sentry.captureMessage(`Audio failed to load: ${audio}`);
    }

    const handleEnded = () => {
        setIsPlaying(false);
        hasTrackedPlay.current = false;
        trackAudioFinished(name ?? '');
    }

    if (hasError) {
        return (
            <Box width='100%' sx={{
                backgroundColor: palette.background.paper,
                borderRadius: '50px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                border: 'none',
                boxShadow: 'none',
                minHeight: '60px',
                px: 2
            }}>
                <Typography sx={{
                    fontFamily: 'Manrope, sans-serif',
                    fontSize: '14px',
                    color: palette.text.dark,
                }}>
                    {t('common.audio-unavailable')}
                </Typography>
            </Box>
        );
    }

    return (
        <Box width='100%' sx={{
            backgroundColor: palette.background.paper,
            borderRadius: '50px',
            display: 'flex',
            alignItems: 'center',
            border: 'none',
            boxShadow: 'none',
            minHeight: '60px'
        }}>
            <Box>
                <audio
                    ref={audioRef}
                    src={createImageUrl(audio)}
                    onLoadedMetadata={handleLoadeMetaData}
                    onTimeUpdate={handleTimeUpdate}
                    onError={handleAudioError}
                    onEnded={handleEnded}
                />
            </Box>

            <Box sx={{
                minWidth: '10px',
                textAlign: 'left',
                ml: '16px'
            }}>
                <span style={{
                    fontFamily: 'Manrope, sans-serif',
                    fontSize: '16px',
                    fontWeight: 400,
                    color: palette.text.dark,
                    lineHeight: 'auto',
                    letterSpacing: '0px'
                }}>
                    {formatTime(currentTime)}
                </span>
            </Box>

            <Box sx={{
                width: '160px',
                position: 'relative',
                ml: '10px',
                mr: '10px'
            }}>
                <Box sx={{
                    width: '100%',
                    height: '4px',
                    backgroundColor: palette.border.medium,
                    borderRadius: '2px',
                    position: 'relative',
                    overflow: 'hidden',
                    display: 'block',
                    boxSizing: 'border-box'
                }}>
                    <Box sx={{
                        width: `${(currentTime / duration) * 100}%`,
                        height: '100%',
                        backgroundColor: palette.brand.orange,
                        borderRadius: '2px',
                        transition: 'width 0.1s ease',
                        display: 'block',
                        boxSizing: 'border-box'
                    }} />
                </Box>
            </Box>

            <Box sx={{
                minWidth: '20px',
                textAlign: 'left',
                mr: '10px'
            }}>
                <span style={{
                    fontFamily: 'Manrope, sans-serif',
                    fontSize: '16px',
                    fontWeight: 400,
                    color: palette.text.dark,
                    lineHeight: 'auto',
                    letterSpacing: '0px'
                }}>
                    {formatTime(duration)}
                </span>
            </Box>

            <IconButton
                aria-label={isPlaying ? 'Pause' : 'Play'}
                disabled={duration === 0}
                onClick={() => setIsPlaying(!isPlaying)}
                sx={{
                    width: '40px',
                    height: '40px',
                    backgroundColor: 'transparent',
                    border: `1px solid ${palette.border.medium}`,
                    color: '#000000',
                    '&:hover': {
                        backgroundColor: palette.surface.grayLight
                    },
                    '&:disabled': {
                        backgroundColor: palette.surface.gray,
                        color: palette.border.input
                    }
                }}
            >
                {isPlaying ? (
                    <Box sx={{
                        width: '20px',
                        height: '20px',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        gap: '3px'
                    }}>
                        <Box sx={{
                            width: '3px',
                            height: '16px',
                            backgroundColor: '#000000',
                            borderRadius: '1px'
                        }} />
                        <Box sx={{
                            width: '3px',
                            height: '16px',
                            backgroundColor: '#000000',
                            borderRadius: '1px'
                        }} />
                    </Box>
                ) : (
                    <Box sx={{
                        width: '0',
                        height: '0',
                        borderLeft: '10px solid #000000',
                        borderTop: '8px solid transparent',
                        borderBottom: '8px solid transparent',
                        marginLeft: '2px'
                    }} />
                )}
            </IconButton>
        </Box>
    );
};

export default WalkingAudioPlayer;
